import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { PageMessage } from '../../../../types/PageMessage';
import type { ProductMessage } from '../../../../types/ProductMessage';
import { queries } from '../../api/queryKeys/queries';

import type { ProductOption } from './ProductOption';
import type { ProductSelectProps } from './ProductSelect';
import { ProductSelectControl } from './ProductSelectControl';
import { useProductSelectOptions } from './useProductSelectOptions';

interface ProductSelectWithSuggestedProductProps extends ProductSelectProps {
    suggestedProductName: string;
}

export const ProductSelectWithSuggestedProduct = ({
    defaultProducts,
    excludedProductIds,
    hideSuccessorProduct = false,
    onChange,
    suggestedProductName,
    value,
    vesselId,
    ...rest
}: ProductSelectWithSuggestedProductProps) => {
    const queryClient = useQueryClient();

    const { cacheSelectedValue, createProductOption, loadProductById, loadProducts, selectedValue } =
        useProductSelectOptions({
            defaultProducts,
            excludedProductIds,
            value,
            vesselId,
        });

    const loadSuggestedProduct = useCallback(async () => {
        if (!suggestedProductName || value) {
            return null;
        }

        try {
            return await queryClient.ensureQueryData({
                ...queries.products.byName({ productName: suggestedProductName, fuzzy: true }),
                staleTime: Infinity,
            });
        } catch {
            return null;
        }
    }, [queryClient, suggestedProductName, value]);

    const markSuggestedOptionIfAvailable = useCallback(
        (options: ProductOption[], suggestedProduct: ProductMessage | null) => {
            if (!suggestedProduct || value) {
                return options;
            }

            return options.map((option) =>
                option.product.id === suggestedProduct.id ? { ...option, suggested: true } : option
            );
        },
        [value]
    );

    const doesSearchMatchSuggestedProduct = useCallback((searchQuery: string, product: ProductMessage) => {
        const normalizedSearchQuery = searchQuery.trim().toLowerCase();

        if (!normalizedSearchQuery) {
            return true;
        }

        return product.name.toLowerCase().includes(normalizedSearchQuery);
    }, []);

    const loadProductsWithSuggestedProduct = useCallback(
        async (searchQuery: string, page: number): Promise<PageMessage<ProductOption>> => {
            const suggestedProduct = await loadSuggestedProduct();
            const shouldPrependSuggestedProduct =
                page === 0 && !!suggestedProduct && doesSearchMatchSuggestedProduct(searchQuery, suggestedProduct);
            const optionsPage = await loadProducts(searchQuery, page, shouldPrependSuggestedProduct ? 1 : 0);

            if (!suggestedProduct || !shouldPrependSuggestedProduct) {
                return {
                    ...optionsPage,
                    content: markSuggestedOptionIfAvailable(optionsPage.content, suggestedProduct),
                };
            }

            const suggestedOption = createProductOption(suggestedProduct, true);
            const options = markSuggestedOptionIfAvailable(optionsPage.content, suggestedProduct);
            let content = options;

            if (page === 0) {
                content = [suggestedOption, ...options.filter((option) => option.product.id !== suggestedProduct.id)];
            }

            return {
                ...optionsPage,
                content,
                pageable: {
                    ...optionsPage.pageable,
                    size: Math.max(optionsPage.pageable.size, content.length),
                },
                total: Math.max(optionsPage.total, content.length),
            };
        },
        [
            createProductOption,
            doesSearchMatchSuggestedProduct,
            loadProducts,
            loadSuggestedProduct,
            markSuggestedOptionIfAvailable,
        ]
    );

    function updateSelectedValue(option: ProductOption | null) {
        if (option) {
            cacheSelectedValue(option);
        }

        onChange(option);
    }

    return (
        <ProductSelectControl
            {...rest}
            hideSuccessorProduct={hideSuccessorProduct}
            selectedValue={selectedValue}
            onSelectedValueChange={updateSelectedValue}
            loadOptions={loadProductsWithSuggestedProduct}
            loadSelectedOption={loadProductById}
            value={value}
            optionsQueryParams={{
                defaultProductIds: defaultProducts?.map((product) => product.id),
                excludedProductIds,
                suggestedProductName,
                vesselId,
            }}
        />
    );
};
